/**
 * MagicMomentTheatrical Component
 *
 * Premium "magic moment" UI for the generation experience.
 * Designed to feel alive and engaging even when backend only sends
 * phase-level polling updates (SSE fallback scenario).
 *
 * Key features:
 * - Animated pulsing orb as centerpiece
 * - Rotating narrative messages per generation phase
 * - Step-based progress indicators
 * - Smooth progress bar with glow
 * - Celebration overlay on completion
 */

import { useState, useEffect, useRef } from 'react';
import type {
  GenerationProgressEvent,
  AgentRole,
  AgentState,
  GenerationPhase,
} from '../hooks/useGenerationStream';

interface MagicMomentTheatricalProps {
  events: GenerationProgressEvent[];
  currentPhase: GenerationPhase | null;
  agents: Map<AgentRole, AgentState>;
  progress: number;
  isComplete: boolean;
  isFailed: boolean;
  errorMessage: string | null;
  isStreaming: boolean;
}

// Rotating messages per phase — cycles every few seconds to keep UI alive
const PHASE_NARRATIVES: Record<string, { title: string; icon: string; messages: string[] }> = {
  competency_extraction: {
    title: 'Analyzing Requirements',
    icon: '\u{1F50D}',
    messages: [
      'Analyzing your training objectives...',
      'Identifying core competencies...',
      'Mapping skill requirements to outcomes...',
      'Evaluating behavioral indicators...',
      'Structuring competency framework...',
    ],
  },
  parallel_research: {
    title: 'Researching Best Practices',
    icon: '\u{1F4DA}',
    messages: [
      'Researching global best practices...',
      'Analyzing industry standards...',
      'Reviewing domain expertise...',
      'Cross-referencing competency frameworks...',
      'Identifying assessment methodologies...',
    ],
  },
  synthesis: {
    title: 'Synthesizing Findings',
    icon: '\u{1F9E9}',
    messages: [
      'Synthesizing research findings...',
      'Building assessment framework...',
      'Aligning competencies to objectives...',
      'Creating coherent assessment structure...',
      'Mapping to customer needs and local profile...',
    ],
  },
  parallel_content: {
    title: 'Creating Assessment Content',
    icon: '\u{1F3AC}',
    messages: [
      'Designing conversation scenario...',
      'Building AI persona personality...',
      'Engineering scoring rubric...',
      'Crafting the assessment experience...',
      'Defining evaluation criteria...',
    ],
  },
  quality_validation: {
    title: 'Polishing & Quality Check',
    icon: '\u2728',
    messages: [
      'Running quality checks...',
      'Validating rubric alignment...',
      'Checking assessment completeness...',
      'Final polishing...',
      'Almost there...',
    ],
  },
};

// Steps shown as progress indicators
const GENERATION_STEPS = [
  { key: 'competency_extraction', label: 'Analyze' },
  { key: 'parallel_research', label: 'Research' },
  { key: 'synthesis', label: 'Synthesize' },
  { key: 'parallel_content', label: 'Create' },
  { key: 'quality_validation', label: 'Polish' },
];

const PHASE_ORDER = GENERATION_STEPS.map((s) => s.key);

function getStepStatus(stepKey: string, currentPhase: string | null): 'done' | 'active' | 'pending' {
  if (!currentPhase || currentPhase === 'completed') {
    return currentPhase === 'completed' ? 'done' : 'pending';
  }
  const currentIdx = PHASE_ORDER.indexOf(currentPhase);
  const stepIdx = PHASE_ORDER.indexOf(stepKey);
  if (stepIdx < currentIdx) return 'done';
  if (stepIdx === currentIdx) return 'active';
  return 'pending';
}

function useRotatingMessage(phase: GenerationPhase | null): string {
  const [index, setIndex] = useState(0);
  const phaseRef = useRef(phase);

  // Reset index when phase changes
  useEffect(() => {
    if (phase !== phaseRef.current) {
      setIndex(0);
      phaseRef.current = phase;
    }
  }, [phase]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => {
        const messages = phase ? PHASE_NARRATIVES[phase]?.messages : null;
        if (!messages) return 0;
        return (prev + 1) % messages.length;
      });
    }, 3500);
    return () => clearInterval(interval);
  }, [phase]);

  if (!phase) return 'Preparing...';
  const narrative = PHASE_NARRATIVES[phase];
  if (!narrative) return 'Working...';
  return narrative.messages[index % narrative.messages.length] ?? 'Working...';
}

export function MagicMomentTheatrical({
  currentPhase,
  progress,
  isComplete,
  isFailed,
  errorMessage,
}: MagicMomentTheatricalProps) {
  const rotatingMessage = useRotatingMessage(isComplete ? null : isFailed ? null : currentPhase);
  const narrative = currentPhase ? PHASE_NARRATIVES[currentPhase] : null;

  return (
    <div style={{ position: 'relative' }}>
      {/* Keyframe animations */}
      <style>{`
        @keyframes mmt-pulse {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        @keyframes mmt-orbit {
          from { transform: rotate(0deg) translateX(48px) rotate(0deg); }
          to { transform: rotate(360deg) translateX(48px) rotate(-360deg); }
        }
        @keyframes mmt-orbit-reverse {
          from { transform: rotate(360deg) translateX(36px) rotate(-360deg); }
          to { transform: rotate(0deg) translateX(36px) rotate(0deg); }
        }
        @keyframes mmt-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes mmt-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(14, 165, 233, 0.15); }
          50% { box-shadow: 0 0 40px rgba(14, 165, 233, 0.3); }
        }
        @keyframes mmt-progress-shimmer {
          from { transform: translateX(-100%); }
          to { transform: translateX(200%); }
        }
        @keyframes mmt-check-pop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes mmt-confetti-float {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(-60px) rotate(180deg); opacity: 0; }
        }
      `}</style>

      {/* Main card */}
      <div
        style={{
          background: isComplete
            ? 'linear-gradient(135deg, var(--emerald-50) 0%, #ffffff 50%, var(--sky-50) 100%)'
            : isFailed
              ? 'var(--red-50)'
              : 'linear-gradient(135deg, var(--sky-50) 0%, #ffffff 50%, var(--slate-50) 100%)',
          borderRadius: 20,
          padding: '40px 32px 32px',
          border: `1px solid ${isComplete ? 'var(--emerald-100)' : isFailed ? 'var(--red-100)' : 'var(--sky-100)'}`,
          animation: !isComplete && !isFailed ? 'mmt-glow 3s ease-in-out infinite' : 'none',
          transition: 'all 0.5s ease',
        }}
      >
        {/* Animated orb centerpiece */}
        {!isComplete && !isFailed && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
            <div style={{ position: 'relative', width: 120, height: 120 }}>
              {/* Central orb */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--sky-400), var(--sky-600))',
                  animation: 'mmt-pulse 2.5s ease-in-out infinite',
                  boxShadow: '0 0 30px rgba(14, 165, 233, 0.3)',
                }}
              />
              {/* Orbiting dot 1 */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 10,
                  height: 10,
                  marginTop: -5,
                  marginLeft: -5,
                  borderRadius: '50%',
                  background: 'var(--sky-300)',
                  animation: 'mmt-orbit 4s linear infinite',
                }}
              />
              {/* Orbiting dot 2 */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 7,
                  height: 7,
                  marginTop: -3.5,
                  marginLeft: -3.5,
                  borderRadius: '50%',
                  background: 'var(--slate-300)',
                  animation: 'mmt-orbit-reverse 5s linear infinite',
                }}
              />
              {/* Orbiting dot 3 */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 6,
                  height: 6,
                  marginTop: -3,
                  marginLeft: -3,
                  borderRadius: '50%',
                  background: 'var(--sky-200)',
                  animation: 'mmt-orbit 6.5s linear infinite',
                  animationDelay: '-2s',
                }}
              />
            </div>
          </div>
        )}

        {/* Completion check mark */}
        {isComplete && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--emerald-500), var(--emerald-600))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'mmt-check-pop 0.5s ease-out',
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
              }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </div>
        )}

        {/* Failure icon */}
        {isFailed && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--red-500), var(--red-600))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)',
              }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
          </div>
        )}

        {/* Phase title & rotating message */}
        <div style={{ textAlign: 'center', marginBottom: isComplete || isFailed ? 16 : 28 }}>
          <h3
            style={{
              margin: '0 0 8px',
              fontSize: 22,
              fontWeight: 700,
              color: isComplete
                ? 'var(--emerald-700)'
                : isFailed
                  ? 'var(--red-700)'
                  : 'var(--slate-900)',
            }}
          >
            {isComplete
              ? 'Your Assessment is Ready!'
              : isFailed
                ? 'Generation Failed'
                : narrative?.title ?? 'Preparing...'}
          </h3>
          <p
            key={rotatingMessage}
            style={{
              margin: 0,
              fontSize: 15,
              color: isComplete
                ? 'var(--emerald-600)'
                : isFailed
                  ? 'var(--red-600)'
                  : 'var(--slate-500)',
              animation: !isComplete && !isFailed ? 'mmt-fade-in 0.4s ease-out' : 'none',
              minHeight: 22,
            }}
          >
            {isComplete
              ? 'Review the generated content and make any adjustments before publishing.'
              : isFailed
                ? errorMessage ?? 'Something went wrong during generation.'
                : rotatingMessage}
          </p>
        </div>

        {/* Step indicators */}
        {!isComplete && !isFailed && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 4,
              marginBottom: 28,
            }}
          >
            {GENERATION_STEPS.map((step) => {
              const status = getStepStatus(step.key, currentPhase);
              return (
                <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: status === 'active' ? 40 : 28,
                        height: 5,
                        borderRadius: 3,
                        background:
                          status === 'done'
                            ? 'var(--sky-500)'
                            : status === 'active'
                              ? 'linear-gradient(90deg, var(--sky-400), var(--sky-600))'
                              : 'var(--slate-200)',
                        transition: 'all 0.5s ease',
                      }}
                    />
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: status === 'active' ? 600 : 400,
                        color:
                          status === 'active'
                            ? 'var(--sky-600)'
                            : status === 'done'
                              ? 'var(--slate-500)'
                              : 'var(--slate-300)',
                        marginTop: 6,
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {step.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Progress bar */}
        {!isComplete && !isFailed && (
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--slate-400)' }}>
                Progress
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sky-600)' }}>
                {Math.round(progress)}%
              </span>
            </div>
            <div
              style={{
                width: '100%',
                height: 6,
                background: 'var(--slate-100)',
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: `${Math.max(progress, 3)}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, var(--sky-400), var(--sky-500))',
                  borderRadius: 3,
                  transition: 'width 0.8s ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Shimmer effect on progress bar */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '50%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    animation: 'mmt-progress-shimmer 2s ease-in-out infinite',
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Failure action */}
        {isFailed && (
          <div
            style={{
              marginTop: 8,
              padding: '12px 16px',
              background: 'white',
              borderRadius: 12,
              border: '1px solid var(--red-100)',
              fontSize: 13,
              color: 'var(--red-600)',
              textAlign: 'center',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* Completion — subtle decorative elements */}
        {isComplete && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 4 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: i % 2 === 0 ? 'var(--sky-300)' : 'var(--emerald-300)',
                  animation: `mmt-confetti-float 1.5s ease-out ${i * 0.15}s forwards`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
